import {createSlice, createAsyncThunk, PayloadAction} from "@reduxjs/toolkit";
import {ILoginFormValues} from "../../../pages/auth/login/i-login";
import {AnalyticsEvent} from "../../../utils/analytics";
import {normalizeV2CoachAuthResponse} from "../auth-normalizer";
import {UserInfo} from "../i-auth";
import {
  googleAuthCallbackService,
  loginStandardUserService,
  logoutUserService,
  verifyTwoFactorLoginService,
} from "./user-login-service";

interface ITwoFactorChallengeState {
  required: boolean;
  challengeToken: string | null;
  method: "email" | "totp" | null;
  // Seconds left before the backend allows an email OTP resend. Only sent when
  // an existing challenge is re-issued; null means a fresh OTP was just sent.
  cooldownRemainingSeconds: number | null;
}

interface IInitialState {
  userInfo: UserInfo | null;
  isError: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  message: string;
  twoFactorChallenge: ITwoFactorChallengeState;
}

const initialTwoFactorChallengeState: ITwoFactorChallengeState = {
  required: false,
  challengeToken: null,
  method: null,
  cooldownRemainingSeconds: null,
};

const initialState: IInitialState = {
  userInfo: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
  twoFactorChallenge: initialTwoFactorChallengeState,
};

/**
 * A login response the app can't act on — no access token — must never become
 * `userInfo`. It gets persisted, and every post-login effect gates on
 * `userInfo?.accessToken`, so storing one strands the user on a login form that
 * reports neither success nor failure. This is what wedged people during the
 * 2FA rollback, when the API answered with a challenge payload that the build
 * of the day had no branch for.
 */
const rejectTokenlessLogin = (state: IInitialState) => {
  state.isError = true;
  state.isSuccess = false;
  state.userInfo = null;
  state.message = "We couldn't complete your login. Please try again.";
};
//login standard user
export const loginStandardUser = createAsyncThunk(
  "userLogin/loginStandardUser",
  async (user: ILoginFormValues, thunkAPI) => {
    try {
      return await loginStandardUserService(user);
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);
// exchange a Google OAuth code + state (from the redirect landing page) for a session
export const loginWithGoogleCallback = createAsyncThunk(
  "userLogin/loginWithGoogleCallback",
  async (payload: {code: string; state: string; timeZone?: string}, thunkAPI) => {
    try {
      return await googleAuthCallbackService(payload);
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);
// verify 2FA OTP and complete login
export const verifyTwoFactorLogin = createAsyncThunk(
  "userLogin/verifyTwoFactorLogin",
  async (payload: {challengeToken: string; twofaCode: string}, thunkAPI) => {
    try {
      const result = await verifyTwoFactorLoginService(payload);
      return normalizeV2CoachAuthResponse(result.data);
    } catch (error: any) {
      const message =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);
//Logout user
export const logoutUser = createAsyncThunk(
  "userLogin/logoutUser",
  async () => await logoutUserService()
);

export const userLoginSlice = createSlice({
  name: "userLogin",
  initialState,
  reducers: {
    resetUserLoginState: (state) => {
      state.isError = false;
      state.isLoading = false;
      state.isSuccess = false;
      state.message = "";
      // state.userInfo = null;
    },
    userLoginLogoutReset: (state) => {
      state.isError = false;
      state.isLoading = false;
      state.isSuccess = false;
      state.message = "";
      state.userInfo = null;
    },
    clearTwoFactorChallenge: (state) => {
      state.twoFactorChallenge = initialTwoFactorChallengeState;
    },
    updateLoginState: (state, action: PayloadAction<UserInfo | null>) => {
      state.userInfo = action.payload;
      state.isError = false;
      state.isLoading = false;
      state.isSuccess = true;
      state.message = "";
      // state.userInfo = null;
    },
    switchUserContext: (
      state,
      action: {payload: {newToken: string; newIsCustomer: boolean}}
    ) => {
      if (state.userInfo) {
        const currentAccessToken = state.userInfo.accessToken;

        // Swap tokens: save current accessToken to the appropriate field before switching
        if (action.payload.newIsCustomer) {
          // Switching to customer: save coach token (current accessToken) to coachToken
          state.userInfo.coachToken = currentAccessToken;
        } else {
          // Switching to coach: save customer token (current accessToken) to customerToken
          state.userInfo.customerToken = currentAccessToken;
        }

        // Set the new token and context
        state.userInfo.accessToken = action.payload.newToken;
        state.userInfo.isCustomer = action.payload.newIsCustomer;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginStandardUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginStandardUser.fulfilled, (state, action) => {
        state.isLoading = false;
        // Honoured whatever IS_TWO_FACTOR_FEATURE_ENABLED says: the backend
        // decides who must pass 2FA, and the code step is the only way in for
        // an account that has it on. Dropping the challenge here locks that
        // coach out behind a "try again" that can never succeed.
        if (action.payload?.data?.twoFactorRequired) {
          state.twoFactorChallenge = {
            required: true,
            challengeToken: action.payload.data.challengeToken ?? null,
            method: action.payload.data.twoFactorMethod ?? null,
            cooldownRemainingSeconds:
              action.payload.data.cooldownRemainingSeconds ?? null,
          };
          return;
        }
        if (!action.payload?.data?.accessToken) {
          rejectTokenlessLogin(state);
          AnalyticsEvent("Standard Login", "Standard Login Failure");
          return;
        }
        state.isSuccess = true;
        state.userInfo = {
          ...action.payload.data,
          isCustomer: false,
          hasCoachAccount: action.payload.data.hasCoachAccount ?? true,
        };
        AnalyticsEvent("Standard Login", "Standard Login Success");
      })
      .addCase(loginStandardUser.rejected, (state, action: any) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.userInfo = null;
        AnalyticsEvent("Standard Login", "Standard Login Failure");
      })
      .addCase(loginWithGoogleCallback.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginWithGoogleCallback.fulfilled, (state, action) => {
        state.isLoading = false;
        // Honoured regardless of the flag, as for password login above.
        if (action.payload?.data?.twoFactorRequired) {
          state.twoFactorChallenge = {
            required: true,
            challengeToken: action.payload.data.challengeToken ?? null,
            method: action.payload.data.twoFactorMethod ?? null,
            cooldownRemainingSeconds:
              action.payload.data.cooldownRemainingSeconds ?? null,
          };
          return;
        }
        if (!action.payload?.data?.accessToken) {
          rejectTokenlessLogin(state);
          AnalyticsEvent("Social Login", "Social Login Failure");
          return;
        }
        state.isSuccess = true;
        state.userInfo = {
          ...action.payload.data,
          isCustomer: false,
          hasCoachAccount: action.payload.data.hasCoachAccount ?? true,
        };
        AnalyticsEvent("Google Login", "Google Login Success");
      })
      .addCase(loginWithGoogleCallback.rejected, (state, action: any) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.userInfo = null;
        AnalyticsEvent("Google Login", "Google Login Failure");
      })
      .addCase(verifyTwoFactorLogin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyTwoFactorLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.userInfo = {
          ...action.payload,
          isCustomer: false,
          hasCoachAccount: action.payload.hasCoachAccount ?? true,
        };
        state.twoFactorChallenge = initialTwoFactorChallengeState;
        AnalyticsEvent("Two-factor login", "Two-factor login success");
      })
      .addCase(verifyTwoFactorLogin.rejected, (state, action: any) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        AnalyticsEvent("Two-factor login", "Two-factor login failure");
      });
    // .addCase(activateStandardUser.pending, (state) => {
    //   state.isLoading = true;
    // })
    // .addCase(activateStandardUser.fulfilled, (state, action) => {
    //   state.isSuccess = true;
    //   state.userInfo = action.payload.data;
    // })
    // .addCase(activateStandardUser.rejected, (state) => {
    //   state.userInfo = null;
    // })
    // .addCase(logoutUser.fulfilled, (state) => {
    //   state.userInfo = null;
    // });
  },
});

export const {
  resetUserLoginState,
  updateLoginState,
  userLoginLogoutReset,
  switchUserContext,
  clearTwoFactorChallenge,
} = userLoginSlice.actions;

export default userLoginSlice.reducer;
